import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  fetchScratchCatalog,
  fetchMyScratchStats,
  type ScratchCatalogItem,
  type ScratchPlayerStats,
} from '../../services/scratchLotteryService'
import { resolveScratchTheme } from './scratchLotteryThemes'
import './scratchLottery.css'

const formatBaht = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const ScratchLotteryLobbyPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<ScratchCatalogItem[]>([])
  const [masterVisible, setMasterVisible] = useState(true)
  const [stats, setStats] = useState<ScratchPlayerStats | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const [catalogRes, statsRes] = await Promise.all([
          fetchScratchCatalog(),
          fetchMyScratchStats().catch(() => null),
        ])
        if (cancelled) return
        setMasterVisible(catalogRes.data?.userEntryVisible !== false)
        setItems(Array.isArray(catalogRes.data?.items) ? catalogRes.data.items : [])
        if (statsRes?.data) setStats(statsRes.data)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'โหลดรายการหวยขูดไม่สำเร็จ')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="scr-lotto scr-lotto--lobby">
      <header className="scr-lotto__hero">
        <div className="scr-lotto__logo" aria-hidden>
          <span className="scr-lotto__logo-mega">MEGA</span>
          <span className="scr-lotto__logo-sub">SCRATCH</span>
        </div>
        <div className="scr-lotto__hero-text">
          <h1 className="scr-lotto__title">หวยขูดเมกา</h1>
          <p className="scr-lotto__subtitle">เลือกชุดที่ต้องการ · หลายธีม หลายราคา</p>
        </div>
      </header>

      {stats && stats.ticketsBought > 0 && (
        <section className="scr-lotto__stats" aria-label="สถิติการเล่นหวยขูด">
          <div className="scr-lotto__stats-grid">
            <div className="scr-lotto__stat">
              <span>ซื้อแล้ว</span>
              <strong>{stats.ticketsBought.toLocaleString('th-TH')} ใบ</strong>
            </div>
            <div className="scr-lotto__stat">
              <span>ใช้ไป</span>
              <strong>{formatBaht(stats.totalSpent)} ฿</strong>
            </div>
            <div className="scr-lotto__stat">
              <span>ได้รางวัลรวม</span>
              <strong>{formatBaht(stats.totalPrizes)} ฿</strong>
            </div>
            <div className={`scr-lotto__stat${stats.netProfit >= 0 ? ' is-plus' : ' is-minus'}`}>
              <span>สุทธิ</span>
              <strong>
                {stats.netProfit >= 0 ? '+' : ''}
                {formatBaht(stats.netProfit)} ฿
              </strong>
            </div>
          </div>
        </section>
      )}

      {loading && (
        <div className="scr-lotto__panel scr-lotto__panel--center">
          <i className="fas fa-spinner fa-spin scr-lotto__spinner" aria-hidden />
          <p>กำลังโหลดชุดหวยขูด...</p>
        </div>
      )}

      {!loading && error && (
        <div className="scr-lotto__panel scr-lotto__panel--center">
          <p className="scr-lotto__error">{error}</p>
          <button type="button" className="scr-lotto__btn scr-lotto__btn--ghost" onClick={() => window.location.reload()}>
            ลองใหม่
          </button>
        </div>
      )}

      {!loading && !error && !masterVisible && (
        <div className="scr-lotto__panel scr-lotto__panel--center">
          <p>หวยขูดเมกายังปิดขายชั่วคราว</p>
        </div>
      )}

      {!loading && !error && masterVisible && items.length === 0 && (
        <div className="scr-lotto__panel scr-lotto__panel--center">
          <p>ยังไม่มีชุดเปิดขาย</p>
        </div>
      )}

      {!loading && !error && masterVisible && items.length > 0 && (
        <div className="scr-lotto__catalog" role="list">
          {items.map((item) => {
            const theme = resolveScratchTheme(item.theme)
            const open = item.salesOpen
            return (
              <article
                key={item.productId}
                className={`scr-lotto__catalog-card${open ? '' : ' is-soldout'}`}
                role="listitem"
                style={{ '--scr-accent': theme.accent } as React.CSSProperties}
              >
                <div
                  className="scr-lotto__catalog-art"
                  style={{ backgroundImage: `url(${theme.header}), url(${theme.bg})` }}
                  aria-hidden
                />
                <div className="scr-lotto__catalog-body">
                  <h2 className="scr-lotto__catalog-name">{item.name}</h2>
                  {item.description ? (
                    <p className="scr-lotto__catalog-desc">{item.description}</p>
                  ) : null}
                  <div className="scr-lotto__catalog-meta">
                    <strong>{formatBaht(item.ticketPrice)} ฿ / ใบ</strong>
                    <span>
                      {open
                        ? `เหลือ ${Number(item.availableTickets || 0).toLocaleString('th-TH')} ใบ`
                        : 'หมดแล้ว'}
                    </span>
                  </div>
                  {item.minPrize != null && item.maxJackpot != null ? (
                    <p className="scr-lotto__catalog-range">
                      รางวัลประมาณ {formatBaht(item.minPrize)}–{formatBaht(item.maxJackpot)} ฿
                    </p>
                  ) : null}
                  {open ? (
                    <Link
                      to={`/points/games/scratch-lottery/${encodeURIComponent(item.productId)}`}
                      className="scr-lotto__btn scr-lotto__btn--buy scr-lotto__catalog-cta"
                    >
                      เลือกชุดนี้
                    </Link>
                  ) : (
                    <button type="button" className="scr-lotto__btn scr-lotto__btn--ghost" disabled>
                      หมดสต็อก
                    </button>
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}

      <div className="scr-lotto__actions">
        <Link to="/history/games/scratch-lottery" className="scr-lotto__btn scr-lotto__btn--ghost">
          <i className="fas fa-history" aria-hidden /> ประวัติหวยขูด
        </Link>
      </div>
    </div>
  )
}

export default ScratchLotteryLobbyPage
